import type { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  FileFolderItem,
  MissionDetailsSelection,
  RunDetailsSelection,
  SidePanelTabName,
  useCapabilityRegistryContext,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import {
  MenuPosition,
  VerticalTab,
  VerticalTabPanel,
} from '@trimble/ui-components/solidjs';
import { MeasurementsListPanel } from '@trimble/measurements-list';
import {
  FreeViewCapability,
  PointCloudStylingCapability,
} from '@trimble/web3d-capabilities';
import { FitToViewMeasurementCapabilityCoordinator } from '@trimble/web3d';
import {
  MeasurementsCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import { createEffect, createMemo, createSignal, JSX, Show } from 'solid-js';
import { ProjectDataTab } from './ProjectDataTab';
import { StylesTab } from './StylesTab';
import { IssueManagementTab } from './IssueManagementTab';
import { useLayersSidePanelTab } from './layersSidePanelTab';
import { useWorkspacesSidePanelTab } from './workspacesSidePanelTab';
import { useViewsSidePanelTab } from './viewsSidePanelTab';

export const SidePanel = (props: {
  selectedTab?: SidePanelTabName;
  onTabChange?(tab: SidePanelTabName, isOpen: boolean): void;
  onViewDetailsSelect?(view: ViewDetailsResponse): void;
  onCreateViewClick?(): void;
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
  issueManagementCreateTopicRequest?: {
    readonly type: 'create-topic';
    readonly id: number;
  };
  onIssueManagementCreateTopicRequestConsumed?(): void;
}): JSX.Element => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const workspacesSidePanelTab = useWorkspacesSidePanelTab();
  const layersSidePanelTab = useLayersSidePanelTab();
  const viewsTab = useViewsSidePanelTab(props);

  const measurementsProvider = capabilityRegistry.get(
    MeasurementsCapabilityProvider
  );
  const fitToViewMeasurementCapabilityCoordinator = capabilityRegistry.get(
    FitToViewMeasurementCapabilityCoordinator
  );
  const pointCloudStylingProvider = capabilityRegistry.get(
    PointCloudStylingCapabilityProvider
  );
  const pointCloudStylingCapability = capabilityRegistry.get(
    PointCloudStylingCapability
  );
  const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider);

  const pointCloudStyling = createMemo(() => {
    const provider = pointCloudStylingProvider();
    const capability = pointCloudStylingCapability();
    return provider && capability ? { provider, capability } : undefined;
  });

  const toggleSelectedMeasurement = (measurementId: string): void => {
    const provider = measurementsProvider();
    if (!provider) return;

    const setMeasurementId =
      measurementId === provider.selectedItemId() ? null : measurementId;

    void provider.setSelectedItemId(setMeasurementId);
    if (setMeasurementId === null) return;

    const coordinator = fitToViewMeasurementCapabilityCoordinator();
    if (!coordinator) return;

    coordinator.fitMeasurementToView(setMeasurementId, [FreeViewCapability]);
  };

  const [requestedTab, setRequestedTab] = createSignal<SidePanelTabName>();

  const hiddenRequestedTab = createMemo(() => {
    const tab = requestedTab();
    const hidden =
      (tab === SidePanelTabName.Workspaces &&
        workspacesSidePanelTab().length === 0) ||
      (tab === SidePanelTabName.Layers && layersSidePanelTab().length === 0);
    return hidden ? tab : undefined;
  });

  const selectedTab = createMemo(() =>
    hiddenRequestedTab() === undefined ? requestedTab() : undefined
  );

  const handleTabChange = (tab: SidePanelTabName, isOpen: boolean): void => {
    setRequestedTab(isOpen ? tab : undefined);
    props.onTabChange?.(tab, isOpen);
  };

  createEffect(() => {
    setRequestedTab(props.selectedTab);
  });

  // The displayed tab is derived. Tell the parent to drop a request for a tab
  // that is not available, so that tab stays closed when it appears again.
  createEffect(() => {
    const hiddenTab = hiddenRequestedTab();
    if (hiddenTab !== undefined) {
      props.onTabChange?.(hiddenTab, false);
    }
  });

  const projectDataTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.ProjectData,
    tooltip: {
      text: t('mobileMapping.projectData.projectData'),
      position: 'right',
    },
    icon: 'master_data',
    iconClass: 'modus-icons',
    content: () => (
      <ProjectDataTab
        onModelDetailsSelect={props.onModelDetailsSelect}
        onRunDetailsSelect={props.onRunDetailsSelect}
        onMissionDetailsSelect={props.onMissionDetailsSelect}
      />
    ),
  };

  const stylesTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.Styles,
    tooltip: {
      text: t('mobileMapping.styles.header'),
      position: 'right',
    },
    icon: 'tc-icon-color',
    content: () => (
      <Show when={pointCloudStyling()} keyed>
        {(data) => (
          <StylesTab
            pointCloudStylingProvider={data.provider}
            pointCloudStylingCapability={data.capability}
            isVisible={() => selectedTab() === SidePanelTabName.Styles}
          />
        )}
      </Show>
    ),
  };

  const measurementsTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.Measurements,
    tooltip: {
      text: t('measurements.measurements'),
      position: 'right',
    },
    icon: 'tc-icon-measure',
    content: () => (
      <Show when={measurementsProvider()} keyed>
        {(provider) => (
          <MeasurementsListPanel
            measurementsProvider={provider}
            onMeasurementClick={toggleSelectedMeasurement}
          />
        )}
      </Show>
    ),
  };

  const issueManagementTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.IssueManagement,
    tooltip: {
      text: t('mobileMapping.issueManagement.title'),
      position: 'right',
    },
    icon: 'tc-icon-bcf',
    preload: true,
    content: () => (
      <Show when={viewsProvider()} keyed>
        {(provider) => (
          <IssueManagementTab
            viewsProvider={provider}
            createTopicRequest={props.issueManagementCreateTopicRequest}
            onCreateTopicRequestConsumed={
              props.onIssueManagementCreateTopicRequestConsumed
            }
          />
        )}
      </Show>
    ),
  };

  const tabs = createMemo<VerticalTab<SidePanelTabName>[]>(() => [
    projectDataTab,
    ...workspacesSidePanelTab(),
    ...layersSidePanelTab(),
    viewsTab,
    stylesTab,
    measurementsTab,
    issueManagementTab,
  ]);

  return (
    <div class="h-full w-full">
      <VerticalTabPanel
        tabs={tabs()}
        menuPosition={MenuPosition.LEFT}
        selectedTab={selectedTab()}
        onTabToggle={handleTabChange}
      />
    </div>
  );
};
